import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { message } from "antd";
import {
  Credentials,
  LogOut,
} from "../../../shared/screen-components/LoginComponent/LoginComponent.types";
import { ENV } from "../../../config";
import { TOAST_DEFAULT_ERROR_MESSAGE, SESSION_USERNAME } from "../../../shared/utils/CommonConstants";
import {
  REFRESH_TOKEN_KEY,
  TENANT_NEMENIC_KEY,
  TOKEN_KEY,
} from "../../../shared/utils/SessionConstants";
import DeliverableService from "../../DeliverableView/services/DeliverableService";

interface DecodedToken {
  UserId: string;
  OrganizationId: string;
  FirstName: string;
  LastName: string;
  TenantId: string;
}
declare global {
  interface Window {
    tokenRefreshTimer: NodeJS.Timeout | undefined;
  }
}
const headers = {
  "Content-Type": "application/json",
  tenant: `${ENV.TENANT_ID}`,
};

const TOKEN_REFRESH_INTERVAL = (3 * 60 * 60 - 60) * 1000; // 2h 59m
const deliverableService = new DeliverableService();
const decodeAccessToken = (accessToken: string): DecodedToken | null => {
  try {
    const decodedToken: DecodedToken = jwtDecode(accessToken);
    const UserId = decodedToken.UserId;
    const OrganizationId = decodedToken.OrganizationId;
    const FirstName = decodedToken.FirstName;
    const LastName = decodedToken.LastName;
    const TenantId = decodedToken.TenantId;
    const UserName = `${FirstName} ${LastName}`;
    localStorage.setItem(SESSION_USERNAME, UserName);
    return { UserId, OrganizationId, FirstName, LastName, TenantId };
  } catch (error) {
    return null;
  }
};

export const LoginService = async (credentials: Credentials) => {
  const apiUrl = `${ENV.API_URL}${ENV.APPLICATION_AUTHENTICATION_URL}`;

  try {
    const response = await axios.post(apiUrl, credentials, { headers });
    const accessToken = response.data.token;

    const decodedToken = decodeAccessToken(accessToken);

    if (decodedToken !== null) {
      const { UserId, OrganizationId, TenantId } = decodedToken;
      localStorage.setItem(TOKEN_KEY, accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refreshToken);
      localStorage.setItem(TENANT_NEMENIC_KEY, TenantId);

      scheduleTokenRefresh();

      // Non-blocking: feature/permission provisioning may be incomplete on a fresh
      // tenant. Navigation proceeds regardless; features default to [] if fetch fails.
      // TODO: fully provision user_feature_mapping before production use.
      try {
        const features = await deliverableService.getAllFeatues(
          UserId,
          OrganizationId,
        );
        localStorage.setItem("features", JSON.stringify(features));
      } catch (featureError) {
        console.warn("Feature fetch failed, defaulting to empty feature set:", featureError);
        localStorage.setItem("features", "[]");
      }
    }

    return response;
  } catch (error) {
    message.error(TOAST_DEFAULT_ERROR_MESSAGE);
    throw error;
  }
};
const scheduleTokenRefresh = () => {
  if (!ENV.API_URL || !ENV.APPLICATION_AUTHENTICATION_REFRESH_URL) {
    console.warn("Token refresh URL not configured — refresh disabled");
    return;
  }

  clearTimeout(window.tokenRefreshTimer);

  const refreshFunction = () => refreshToken(headers, { suppressError: true });
  window.tokenRefreshTimer = setTimeout(() => {
    refreshFunction();
  }, TOKEN_REFRESH_INTERVAL);
};

export const refreshToken = async (
  headers: any,
  options: { suppressError?: boolean } = {},
) => {
  try {
    let refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

    const refreshResponse = await axios.post(
      `${ENV.API_URL}${ENV.APPLICATION_AUTHENTICATION_REFRESH_URL}`,
      { refreshToken },
      { headers },
    );

    localStorage.setItem(
      REFRESH_TOKEN_KEY,
      refreshResponse.data.refreshToken,
    );

    if (refreshResponse.data.token) {
      localStorage.setItem(TOKEN_KEY, refreshResponse.data.token);
    }

    scheduleTokenRefresh();
    return refreshResponse.data.token;
  } catch (error) {
    message.error(TOAST_DEFAULT_ERROR_MESSAGE);
    if (!options.suppressError) {
      throw error;
    }

    return undefined;
  }
};

export const LogoutService = async (logoutToken: LogOut) => {
  const apiUrl = `${ENV.API_URL}${ENV.APPLICATION_LOGOUT_URL}`;

  try {
    const response = await axios.put(apiUrl, logoutToken, { headers });
    return response;
  } catch (error) {
    message.error(TOAST_DEFAULT_ERROR_MESSAGE);
    throw error;
  }
};
